import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ERelationBehaviour } from 'api/meta/client-metaapi';
import { EPropertySettingTypes } from 'api/meta/enums/nodes/properties/EPropertySettingTypes';
import { isPicklistSettings } from 'api/meta/enums/nodes/properties/EPropertySettingUtils';
import { EOverrideAccessRights } from 'api/projectTeamConfigurations/client-projectTeamApi';
import { useTemplateQueriesWithErrors } from 'api/queryValidation/hooks/useTemplateQueriesWithErrors';
import type { ValueOf } from 'api/types';
import { useProjectBasePath } from 'app/routes/projects/hooks/useProjectPath';
import { createGetNodePropertyValueFallbackToNodeId } from 'pages/projects/projectPage/nodeQuery/advancedTree/utils/createGetNodePropertyValueFallbackToNodeId';
import {
  GRID_DATE_CELL_FIXED_WIDTH,
  GRID_HEADER_CELL_REQUIRED_GUTTER,
  GRID_NUMBER_CELL_FIXED_WIDTH,
  GRID_OWNERSHIP_CELL_WIDTH,
  GRID_SEMANTIC_CELL_WIDTH,
  GRID_TEXT_CELL_MAXIMUM_WIDTH,
  GRID_TEXT_CELL_MINIMUM_WIDTH,
  GRID_TEXT_CELL_REQUIRED_GUTTER
} from 'pages/projects/projectPage/nodeQuery/grid/gridconstants';
import { getStringWidth } from 'state/grids/utils/headerCellUtils';
import type { DataLink, DataLinkGroup } from 'state/selectedProject/useOverviews';
import type { CalculatedSemNode } from 'types/data/CalculatedSemNode';
import type { IDictionary } from 'types/data/semNode';
import { getDisplayName } from 'utils/names';
import { mapToRecord } from 'utils/records';
import type { Nullable } from 'utils/values';
import { useShallow } from 'zustand/react/shallow';

import type { PropertyValue } from '../data/dataState';
import type { AccessRightsMap, SimpleAccessRightsMap } from '../grids/gridState';
import type {
  DataQuery,
  DisplayName,
  ITraverseNode,
  ITraverseProperty,
  ITraverseRelation,
  Node_,
  PicklistPropertySettings,
  Property,
  PropertySettings,
  Relation,
  Template
} from '../selectedTemplate/selectedTemplateStore';
import { EPropertyTypes } from '../selectedTemplate/selectedTemplateStore';
import { SELECTED_PROJECT_STORE } from './selectedProjectState';

export const useSelectedProjectTemplateNode = (nodeId: string) => {
  return SELECTED_PROJECT_STORE(useShallow((state) => state.template?.nodes.find((node) => node.id === nodeId)));
};

export const useSelectedProjectTemplateNodes = () => {
  return SELECTED_PROJECT_STORE((state) => state.template?.nodes);
};

/** Ids of hyper nodes (the reified marker node of a many-to-many `HyperRelation`) in the current project. */
export const useSelectedProjectHyperNodeIds = (): Set<string> => {
  const hyperRelations = SELECTED_PROJECT_STORE((state) => state.template?.hyperRelations);

  return useMemo(() => new Set(hyperRelations?.map((hr) => hr.hyperNodeId) ?? []), [hyperRelations]);
};

export const useUserHasEditRightsOnNode = (nodeId: string | undefined): boolean => {
  const accessRightsByRoleMap = SELECTED_PROJECT_STORE((state) => state.nodeAccessRightsByRoleMap);

  const rightsMap = !!nodeId && !!accessRightsByRoleMap ? accessRightsByRoleMap[nodeId] : undefined;
  return rightsMap ? Object.values(rightsMap).some((value) => value === EOverrideAccessRights.edit) : false;
};

export const useUserHasEditRightsOnProperty = (propertyId: string | undefined): boolean => {
  const accessRightsByRoleMap = SELECTED_PROJECT_STORE((state) => state.propertyAccessRightsByRoleMap);

  const rightsMap = !!propertyId && !!accessRightsByRoleMap ? accessRightsByRoleMap[propertyId] : undefined;
  return hasEditAccessRight(rightsMap);
};

/**
 * Plain-function form of the edit-access check, for callers that need to evaluate it per-item in a
 * loop (e.g. building grid columns) where calling the `propertyId`-keyed hook per iteration would
 * violate the Rules of Hooks.
 */
export function hasEditAccessRight(rightsMap: AccessRightsMap[string] | undefined): boolean {
  return rightsMap ? Object.values(rightsMap).some((value) => value === EOverrideAccessRights.edit) : false;
}

export const useSelectedProjectQueries = (nodeId: string | undefined): DataQuery[] => {
  const template = SELECTED_PROJECT_STORE((state) => state.template);

  const [dataQueries, setDataQueries] = useState<DataQuery[]>([]);

  useEffect(() => {
    if (nodeId === undefined) {
      setDataQueries([]);
    }

    const result: DataQuery[] = [];
    template?.queries.forEach((q) => {
      const rootNode = getRootNode(q);

      if (rootNode.targetTemplateNodeId === nodeId) {
        result.push(q);
      }

      setDataQueries([]);
    });

    setDataQueries(result);
  }, [nodeId, template]);

  return dataQueries;
};

export const useSelectedProjectOverviews = () => {
  const template = SELECTED_PROJECT_STORE((state) => state.template);
  const queriesWithErrors = useTemplateQueriesWithErrors(template?.id);

  const nodeWithQueries = useMemo(() => {
    if (!template) {
      return [];
    }

    const filteredQueries = template.queries.filter((q) => !queriesWithErrors.includes(q.id) && q.isOverview);
    const queriesByNode = template.nodes.map((node) => {
      const nodeQueries = filteredQueries
        .filter((q) => q.startNode.targetTemplateNodeId === node.id)
        .map((q) => ({ queryId: q.id, name: q.name }));

      return { node, queries: nodeQueries };
    });

    return queriesByNode;
  }, [template, queriesWithErrors]);

  return nodeWithQueries;
};

export const useSelectedProjectQuery = (queryId: string): DataQuery | undefined => {
  const template = useSelectedProjectTemplate();
  const [result, setResult] = useState<DataQuery>();

  useEffect(() => {
    if (template !== undefined && template.queries !== undefined) {
      const result = template.queries.find((q) => q.id === queryId);

      setResult(result);
    } else {
      setResult(undefined);
    }
  }, [queryId, template]);

  return result;
};

export interface TableSpecification {
  columns: IDictionary<ColumnSpecification>;
  columnMap: IDictionary<number>;
  isHandshake: boolean;
}

export enum GridColumnType {
  AddRootCreate,
  AddRootSelect,
  AddCreate,
  AddSelect,
  PropertyText,
  PropertyNumber,
  PropertyWholeNumber,
  PropertyDate,
  PropertyOwnership,
  PropertyAutoId
}

export interface ColumnSpecification {
  id: string;
  parentColumnId: string | null;
  headerName: string;
  isSemanticEdit: boolean;
  columnType: GridColumnType;
  columnIndex: number;
  nodeTraversal: ITraverseNode;
  propertyTraversal?: Nullable<ITraverseProperty>;
  relationTraversal?: Nullable<ITraverseRelation>;
  localColumnIndex: number;
  branchIndex: number;
  isDisabled: boolean;
  picklistMap: Nullable<PickListMap>;
  width: number;
}

export type PickListMap = { [value: number]: DisplayName[] };

export const CreateTableSpecification = (
  nodeAccessRights: SimpleAccessRightsMap,
  propertyAccessRights: SimpleAccessRightsMap,
  relationAccessRights: SimpleAccessRightsMap,
  templateNodes: Node_[],
  query: DataQuery
) => {
  const columns = {} as IDictionary<ColumnSpecification>;

  if (query === undefined) {
    throw new Error('Query not found');
  }

  const rootNode = getRootNode(query);

  TraverseQuery(
    nodeAccessRights,
    propertyAccessRights,
    relationAccessRights,
    templateNodes,
    query,
    'root',
    rootNode,
    1,
    undefined,
    columns,
    false
  );

  return {
    columns: columns
  } as TableSpecification;
};

function convertRelationBehaviourToColumnType(relation: Relation): GridColumnType {
  if (relation.behaviour === ERelationBehaviour.Select) {
    return GridColumnType.AddSelect;
  }

  if (relation.behaviour === ERelationBehaviour.Create) {
    return GridColumnType.AddCreate;
  }

  throw new Error('Relation behaviour not supported');
}

function TraverseQuery(
  nodeAccessRights: SimpleAccessRightsMap,
  propertyAccessRights: SimpleAccessRightsMap,
  relationsAccessRights: SimpleAccessRightsMap,
  templateNodes: Node_[],
  query: DataQuery,
  edgeId: string,
  currentNode: ITraverseNode,
  branchIndex: number,
  parentColumn: string | undefined,
  columns: IDictionary<ColumnSpecification>,
  isReverse: boolean
) {
  const columnId =
    parentColumn === undefined
      ? 'root'
      : `R${edgeId.replace(/-/g, '').toLocaleLowerCase()}${isReverse ? '_reverse' : ''}`; // HACK beware that edge names must match the server result!
  // replace the dashes in the edge id in order to match the data in the query results

  const targetNode = findTargetNode(templateNodes, currentNode.targetTemplateNodeId);

  if (parentColumn !== undefined) {
    const queryRelation = query.nodes.flatMap((n) => n.relations).find((r) => r.id === edgeId);

    if (queryRelation === undefined) {
      throw Error(`Query edge ${edgeId} not found`);
    }

    const targetRelation = templateNodes
      .flatMap((n) => n.relations)
      .find((r) => r.id === queryRelation.targetRelationId);

    if (targetRelation === undefined) {
      throw new Error('Target relation not found');
    }

    const queryRelationColumnType = convertRelationBehaviourToColumnType(targetRelation);

    columns[columnId] = {
      isSemanticEdit: true,
      columnType: queryRelationColumnType,
      parentColumnId: parentColumn,
      //parentColumn: columns[parentColumn],
      headerName: targetNode.technicalName,
      nodeTraversal: currentNode,
      relationTraversal: queryRelation,
      localColumnIndex: -1,
      branchIndex: branchIndex,
      id: columnId,
      isDisabled: relationsAccessRights[queryRelation.targetRelationId],
      picklistMap: {},
      width: GRID_SEMANTIC_CELL_WIDTH
    } as ColumnSpecification;
  } else {
    columns[columnId] = {
      isSemanticEdit: true,
      columnType: GridColumnType.AddCreate, // TODO: when query is not an overview, this can be select
      columnIndex: 1,
      headerName: targetNode.technicalName,
      nodeTraversal: currentNode,
      localColumnIndex: -1,
      branchIndex: -1,
      id: columnId,
      isDisabled: nodeAccessRights[targetNode.id],
      picklistMap: {},
      width: GRID_SEMANTIC_CELL_WIDTH
    } as ColumnSpecification;
  }

  currentNode.properties
    .filter((p) => p.$type === 'SemantX.MetaApi.Contract.Queries.ReturnProperty, SemantX.MetaApi.Contract.Queries')
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .forEach((prop, localColumnIndex) => {
      const targetProperty = targetNode.properties.find((p) => p.id === prop.targetPropertyId)!;

      const propertyColumnId = `${columnId}_${prop.targetPropertyName}`;

      const columnType = getColumnType(targetProperty.settings, prop.propertyType);
      const columnWidth = getColumnWidth(targetProperty, prop.targetPropertyName, prop.propertyType);

      let picklistMap: PickListMap = {};

      if (isPicklistSettings(targetProperty.settings)) {
        const picklistProperty = targetProperty.settings;

        if (picklistProperty.values !== undefined) {
          picklistMap = Object.entries(picklistProperty.values).reduce<Record<string, DisplayName[]>>(
            (record, [key, value]) => {
              record[key] = value.valueNames;
              return record;
            },
            {}
          );
        }
      }

      columns[propertyColumnId] = {
        isSemanticEdit: false,
        //parentColumn: columns[columnId],
        columnType: columnType,
        parentColumnId: columnId,
        headerName: prop.targetPropertyName,
        nodeTraversal: currentNode,
        propertyTraversal: prop,
        localColumnIndex: localColumnIndex,
        id: propertyColumnId,
        isDisabled: propertyAccessRights[prop.targetPropertyId],
        picklistMap: picklistMap,
        width: columnWidth
      } as ColumnSpecification;
    });

  nextRelations(query, currentNode).forEach((i, edgeIndex) => {
    TraverseQuery(
      nodeAccessRights,
      propertyAccessRights,
      relationsAccessRights,
      templateNodes,
      query,
      i.id,
      i.nextNode,
      edgeIndex,
      columnId,
      columns,
      i.isReverse
    );
  });
}

function findTargetNode(templateNodes: Node_[], nodeId: string) {
  const result = templateNodes.find((n) => n.id === nodeId);

  if (result === undefined) {
    throw Error('Node not found');
  }

  return result;
}

function getColumnType(propertySettings: PropertySettings, propertyType: EPropertyTypes): GridColumnType {
  switch (propertySettings.$type) {
    case EPropertySettingTypes.AutoId:
      return GridColumnType.PropertyAutoId;
    case EPropertySettingTypes.Ownership:
      return GridColumnType.PropertyOwnership;
    case EPropertySettingTypes.Date:
      return GridColumnType.PropertyDate;
    case EPropertySettingTypes.Text:
      return GridColumnType.PropertyText;
    case EPropertySettingTypes.Rational:
      return GridColumnType.PropertyNumber;
    case EPropertySettingTypes.Integer:
      return GridColumnType.PropertyWholeNumber;
  }
  // calculated fields can be either integer or double, check by property type
  switch (propertyType) {
    case EPropertyTypes.Double:
      return GridColumnType.PropertyNumber;
    case EPropertyTypes.Long:
      return GridColumnType.PropertyWholeNumber;
    default:
      return GridColumnType.PropertyText;
  }
}

function getColumnWidth(targetProperty: Property, targetPropertyName: string, propertyType: EPropertyTypes): number {
  const headerText = getDisplayName(targetProperty.displayNames) ?? targetPropertyName;
  const headerTextWidth = getStringWidth(headerText);
  const headerWidth = headerTextWidth + GRID_HEADER_CELL_REQUIRED_GUTTER;

  switch (propertyType) {
    case EPropertyTypes.Date: {
      const dateTextWidth = getStringWidth(new Date().toLocaleDateString());
      const dateFieldWidth = dateTextWidth > headerWidth ? dateTextWidth : headerWidth;

      return (
        (GRID_DATE_CELL_FIXED_WIDTH > dateFieldWidth ? GRID_DATE_CELL_FIXED_WIDTH : dateFieldWidth) +
        GRID_TEXT_CELL_REQUIRED_GUTTER
      );
    }
    case EPropertyTypes.Double:
      if (isPicklistSettings(targetProperty.settings)) break;

      return (
        (GRID_NUMBER_CELL_FIXED_WIDTH > headerWidth ? GRID_NUMBER_CELL_FIXED_WIDTH : headerWidth) +
        GRID_TEXT_CELL_REQUIRED_GUTTER
      );
    default:
      break;
  }

  switch (targetProperty.settings.$type) {
    case EPropertySettingTypes.AutoId: {
      const templateText = 'ABCD-0123456'; // TODO: Calculate it based on the actual text

      const autoIdTextWidth = getStringWidth(templateText);
      const autoIdFieldWidth =
        (autoIdTextWidth > headerWidth ? autoIdTextWidth : headerWidth) + GRID_TEXT_CELL_REQUIRED_GUTTER;

      return GRID_TEXT_CELL_MINIMUM_WIDTH > autoIdFieldWidth ? GRID_TEXT_CELL_MINIMUM_WIDTH : autoIdFieldWidth;
    }
    case EPropertySettingTypes.Picklist: {
      const picklistProperty = targetProperty.settings as PicklistPropertySettings;

      const valueNames = Object.values(picklistProperty.values ?? []).flatMap((v) => v.valueNames);
      const displayNameWidths = valueNames.map((v) => Math.ceil(getStringWidth(v.value)));

      const picklistMaxWidth = Math.max(...displayNameWidths);
      const picklistFieldWidth =
        (picklistMaxWidth > headerWidth ? picklistMaxWidth : headerWidth) + GRID_TEXT_CELL_REQUIRED_GUTTER;

      return GRID_TEXT_CELL_MINIMUM_WIDTH > picklistFieldWidth ? GRID_TEXT_CELL_MINIMUM_WIDTH : picklistFieldWidth;
    }
    case EPropertySettingTypes.Ownership:
      return GRID_OWNERSHIP_CELL_WIDTH;
    default:
      break;
  }

  return GRID_TEXT_CELL_MAXIMUM_WIDTH;
}

function nextRelations(query: DataQuery, currentNode: ITraverseNode): ITraverseRelation[] {
  const result = currentNode.relations.map((r) => {
    const nextNode = query.nodes.find((n) => n.id === r.nextNodeId);
    return { ...r, nextNode: nextNode } as ITraverseRelation;
  });

  return result;
}

// TODO: move to datastate
export function addRowStartAndColumnIndex(
  nodes: CalculatedSemNode[],
  rowStart: number,
  columnIndexByKey: Record<string, number>,
  edgeKeyMap: { [key: string]: string[] },
  edgeKey: string
): CalculatedSemNode[] {
  const result = nodes.map((n) => {
    const edgeKeys = edgeKeyMap[edgeKey] ?? [];
    edgeKeys.forEach((edgeKey) => {
      const edge = n.edges[edgeKey];
      if (!edge) {
        return;
      }

      const columnIndex = columnIndexByKey[edgeKey] ?? 0;
      edge.nodes = addRowStartAndColumnIndex(edge.nodes, rowStart, columnIndexByKey, edgeKeyMap, edgeKey).map(
        (node) => {
          return { ...node, column: columnIndex };
        }
      );
    });

    return { ...n, rowStart: n.rowStart + rowStart };
  });

  return [...result];
}

// TODO: move to data state
export function deepFlattenNodes(calculatedNodes: CalculatedSemNode[]): CalculatedSemNode[] {
  const children = calculatedNodes.flatMap((node) => {
    return Object.values(node.edges).flatMap((edge) => deepFlattenNodes(edge.nodes));
  });

  return [...calculatedNodes, ...children];
}

// TODO: move to data state
export function setOriginalRowIndex(
  calculatedNodes: CalculatedSemNode[],
  startIndex: number,
  edgeKeyMap: { [key: string]: string[] },
  edgeKey: string
): CalculatedSemNode[] {
  const result = calculatedNodes.map((n, index) => {
    const edgeKeys = edgeKeyMap[edgeKey] ?? [];
    edgeKeys.forEach((edgeKey) => {
      const edge: Nullable<ValueOf<CalculatedSemNode['edges']>> = n.edges[edgeKey];
      if (edge) {
        edge.nodes = setOriginalRowIndex(edge.nodes, startIndex + index, edgeKeyMap, edgeKey);
      }
    });
    return { ...n, rowNumber: startIndex + index };
  });

  return result;
}

export const getRootNode = (query: DataQuery) => {
  const nodes = query.nodes;
  const rootNode = nodes.find((n) => n.id === query.startNode.id);

  if (rootNode === undefined) {
    throw new Error('Rootnode not found!');
  }

  return rootNode;
};

export type TemplateNodeDisplayNameAndIcon = {
  name: string;
  icon: string;
  primaryDisplayPropertyName: string;
  getNodeDisplayName: (nodeProperties: Record<string, PropertyValue>) => string;
};

export const useSelectedProjectTemplateNodeDisplayNameAndIcon = (
  nodeId: string | undefined
): TemplateNodeDisplayNameAndIcon => {
  return SELECTED_PROJECT_STORE(({ template }) => {
    if (template === undefined || nodeId === undefined) {
      return {
        name: 'Unknown',
        icon: 'Unknown',
        primaryDisplayPropertyName: 'Id',
        getNodeDisplayName: (nodeProperties: Record<string, PropertyValue>) => {
          return `${nodeProperties['Id']}`;
        }
      };
    } else {
      return getDisplayNameAndIcon(template, nodeId);
    }
  });
};

export function useSelectedProjectNodeDisplayNamesAndIcons(): {
  [technicalName: string]: TemplateNodeDisplayNameAndIcon;
} {
  const template = SELECTED_PROJECT_STORE((state) => state.template);

  // Memoized on the template: building it in the selector returned a new record on every store change.
  return useMemo(() => {
    if (template === undefined) {
      return {};
    }

    return mapToRecord(
      template.nodes,
      (node) => node.technicalName,
      (node) => getDisplayNameAndIcon(template, node.id)
    );
  }, [template]);
}

const getDisplayNameAndIcon = (template: Template, nodeId: string) => {
  const templateNode = template.nodes.find((node) => node.id === nodeId);

  if (templateNode === undefined) {
    throw new Error('Node not found!');
  }

  const displayName = getDisplayName(templateNode.displayNames);

  const primaryProperty = templateNode.properties.find((property) => property.id === templateNode.primaryPropertyId);
  const primaryPropertyTechnicalName = primaryProperty?.technicalName ?? 'Id';

  const autoIdProperty = templateNode.properties.find(
    (property) => property.settings.$type === EPropertySettingTypes.AutoId
  );

  const getNodePropertyValueFallbackToNodeId = createGetNodePropertyValueFallbackToNodeId(
    templateNode,
    primaryPropertyTechnicalName
  );

  if (autoIdProperty !== undefined && displayName !== undefined && autoIdProperty.id !== primaryProperty?.id) {
    return {
      name: displayName,
      icon: templateNode.icon,
      primaryDisplayPropertyName: primaryPropertyTechnicalName,
      getNodeDisplayName: (nodeProperties: Record<string, PropertyValue>) => {
        const displayValue = getNodePropertyValueFallbackToNodeId(nodeProperties);

        return nodeProperties[autoIdProperty.technicalName] !== undefined
          ? `${nodeProperties[autoIdProperty.technicalName]} - ${displayValue}`
          : displayValue;
      }
    };
  }

  if (displayName !== undefined) {
    return {
      name: displayName,
      icon: templateNode.icon,
      primaryDisplayPropertyName: primaryPropertyTechnicalName,
      getNodeDisplayName: (nodeProperties: Record<string, PropertyValue>) => {
        const displayValue = getNodePropertyValueFallbackToNodeId(nodeProperties);

        return displayValue;
      }
    };
  }

  return {
    name: templateNode.technicalName,
    icon: templateNode.icon,
    primaryDisplayPropertyName: primaryPropertyTechnicalName,
    getNodeDisplayName: (nodeProperties: Record<string, PropertyValue>) => {
      const displayValue = getNodePropertyValueFallbackToNodeId(nodeProperties);

      return `${displayValue} 7`;
    }
  };
};

export const useSelectedProjectTemplateRelations = () => {
  return SELECTED_PROJECT_STORE((state) => state.template?.nodes.flatMap((node) => node.relations));
};

export const useSelectedProjectTemplateRelationDisplay = (relationId: string) => {
  const template = SELECTED_PROJECT_STORE((state) => state.template);

  const relation = useMemo(() => {
    if (template) {
      for (const node of template.nodes) {
        for (const relation of node.relations) {
          if (relation.id === relationId) {
            return relation;
          }
        }
      }
    }
  }, [relationId, template]);

  const parentNode = useMemo(() => {
    return template?.nodes.find((n) => n.relations.some((r) => r.id === relationId));
  }, [relationId, template?.nodes]);

  const startNode = useSelectedProjectTemplateNodeDisplayNameAndIcon(parentNode?.id);
  const targetNode = useSelectedProjectTemplateNodeDisplayNameAndIcon(relation?.targetNodeId);

  const result = useMemo(() => {
    const dominantDisplayName = getDisplayName(relation?.displayNames);
    const reverseDisplayName = getDisplayName(relation?.reverseDisplayNames);

    return {
      relation: { ...relation, dominantDisplayName: dominantDisplayName, reverseDisplayName: reverseDisplayName },
      startNode,
      targetNode
    };
  }, [relation, startNode, targetNode]);

  return result;
};

export const useSelectedProjectTemplate = () => {
  const template = SELECTED_PROJECT_STORE((state) => state.template);

  return template!;
};

export const useSelectedProjectTemplateProperties = (): Property[] => {
  const template = SELECTED_PROJECT_STORE((state) => state.template);

  return useMemo(() => template?.nodes.flatMap((n) => n.properties) ?? [], [template]);
};

export const useSelectedProjectTemplateProperty = (propertyId: string): Property | undefined => {
  const templateProperties = useSelectedProjectTemplateProperties();

  return useMemo(() => templateProperties.find((p) => p.id === propertyId), [propertyId, templateProperties]);
};

export function usePickList(picklistId: Nullable<string>) {
  const picklist = usePropertySettings(picklistId);
  // TODO: remove this type casting and validate if the found property settings is actually a picklist.
  return picklist as PicklistPropertySettings | undefined;
}

/** Find and return the property settings by ID. */
const usePropertySettings = (propertyId: Nullable<string>) => {
  // Ensure that it only rerenders when the property settings change.
  const propertySettings = SELECTED_PROJECT_STORE(
    useShallow((state) => {
      const nodes = state.template?.nodes;
      if (!nodes || !propertyId) return;
      return findPropertyById(nodes, propertyId)?.settings;
    })
  );

  return propertySettings;
};

export function findPropertyById(nodes: Node_[], propertyId: string) {
  if (!nodes || !propertyId) return;
  for (const node of nodes) {
    for (const property of node.properties) {
      if (property.id === propertyId) {
        return property;
      }
    }
  }
}

export const useActiveQuery = () => {
  const { queryId } = useParams() as { queryId: string };
  const queries = SELECTED_PROJECT_STORE((state) => state.template?.queries);

  const [activeView, setActiveView] = useState<DataQuery>();

  useEffect(() => {
    const view = queries?.find((v) => v.id === queryId);
    setActiveView(view);
  }, [queryId, queries]);

  return activeView;
};

export const useSelectedProjectTemplateDecompositionRelations = () => {
  const template = SELECTED_PROJECT_STORE((state) => state.template);

  const [decompositionRelations, setDecompositionRelations] = useState<{ parentNode: Node_; relation: Relation }[]>([]);

  useEffect(() => {
    const nodes =
      template?.nodes.flatMap((node) => {
        return node.relations.map((rel) => {
          return { parentNode: node, relation: rel };
        });
      }) ?? [];
    const nodeWithDecompositionRelations = nodes.filter((n) => n.relation.targetNodeId === n.parentNode.id);
    setDecompositionRelations(nodeWithDecompositionRelations);
  }, [template]);

  return decompositionRelations;
};

export const useSelectedProjectViewHeaderDataLinks = (viewId: string) => {
  const views = SELECTED_PROJECT_STORE((state) => state.template?.projectViews);
  const template = SELECTED_PROJECT_STORE((state) => state.template);
  const projectId = SELECTED_PROJECT_STORE((state) => state.projectId);
  const hierarchies = useSelectedProjectTemplateDecompositionRelations();
  const view = views?.find((v) => v.id === viewId);

  const basePath = useProjectBasePath();
  const [headerLinks, setHeaderLinks] = useState<DataLinkGroup[]>([]);

  useEffect(() => {
    const links = view!.headers.map((h) => {
      const overviewDataLinks = h.overviews
        .slice()
        .sort((a, b) => a.item1 - b.item1)
        .map((o) => {
          const overview = template?.queries.find((q) => q.id === o.item2);
          return {
            name: overview?.name,
            link: `${basePath}/views/${viewId}/h/${h.id}/q/${o.item2}`
          } as DataLink;
        });

      const hierarchyDataLinks = h.hierarchyRelations
        .slice()
        .sort((a, b) => a.item1 - b.item1)
        .map((o) => {
          const hierarchy = hierarchies.find((h) => h.relation.id === o.item2);
          return {
            name: getDisplayName(hierarchy?.parentNode.displayNames),
            link: `${basePath}/views/${viewId}/h/${h.id}/n/${hierarchy?.parentNode.id}`
          } as DataLink;
        });

      return {
        name: getDisplayName(h.displayNames),
        dataLinks: [...overviewDataLinks, ...hierarchyDataLinks],
        index: h.index
      } as DataLinkGroup;
    });

    setHeaderLinks(links);
  }, [views, template, hierarchies, projectId, view, viewId, basePath]);

  return headerLinks;
};
